/* eslint-disable complexity */
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { formatCurrency } from '@futrli/localisation';
import { PerformanceMetricsBusinessInsightsComparisonData } from '@futrli-sdk/predict';

import * as styles from '../content.scss';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';

interface Props {
  periodData: PerformanceMetricsBusinessInsightsComparisonData;
}

export const PerformanceToTargetInfoList = ({ periodData }: Props) => {
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();

  return (
    <ul className={styles.bulletList}>
      {/* Sales */}
      <li>
        {Number(periodData.sales.variance) === 0 ? (
          <FormattedMessage id="onboarding.sales_on_target" defaultMessage="Sales on target" />
        ) : (
          <FormattedMessage
            id="onboarding.sales_direction_target"
            defaultMessage="Sales are {direction} target by {changedValue}"
            values={{
              direction:
                Number(periodData.sales.variance) > 0
                  ? intl.formatMessage({ id: 'predict.above', defaultMessage: 'above' })
                  : intl.formatMessage({ id: 'predict.under', defaultMessage: 'under' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.sales.variance) > 0,
                    [styles.negativeChange]: Number(periodData.sales.variance) < 0
                  })}
                >
                  {`${Number(periodData.sales.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.sales.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Costs */}
      <li>
        {Number(periodData.cost_of_sales.variance) === 0 ? (
          <FormattedMessage id="onboarding.costs_on_target" defaultMessage="Costs on target" />
        ) : (
          <FormattedMessage
            id="onboarding.costs_direction_target"
            defaultMessage="Costs are {direction} target by {changedValue}"
            values={{
              direction:
                Number(periodData.cost_of_sales.variance) > 0
                  ? intl.formatMessage({ id: 'predict.above', defaultMessage: 'above' })
                  : intl.formatMessage({ id: 'predict.under', defaultMessage: 'under' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.cost_of_sales.variance) < 0,
                    [styles.negativeChange]: Number(periodData.cost_of_sales.variance) > 0
                  })}
                >
                  {`${Number(periodData.cost_of_sales.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.cost_of_sales.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Expenses */}
      <li>
        {Number(periodData.expenses.variance) === 0 ? (
          <FormattedMessage id="onboarding.expenses_on_target" defaultMessage="Expenses on target" />
        ) : (
          <FormattedMessage
            id="onboarding.expenses_direction_target"
            defaultMessage="Expenses are {direction} target by {changedValue}"
            values={{
              direction:
                Number(periodData.expenses.variance) > 0
                  ? intl.formatMessage({ id: 'predict.above', defaultMessage: 'above' })
                  : intl.formatMessage({ id: 'predict.under', defaultMessage: 'under' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.expenses.variance) < 0,
                    [styles.negativeChange]: Number(periodData.expenses.variance) > 0
                  })}
                >
                  {`${Number(periodData.expenses.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.expenses.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Net profit */}
      <li>
        {Number(periodData.net_profit.variance) === 0 ? (
          <FormattedMessage id="onboarding.net_profit_on_target" defaultMessage="Net profit on target" />
        ) : (
          <FormattedMessage
            id="onboarding.net_profit_direction_target"
            defaultMessage="Net profit is {direction} target by {changedValue}"
            values={{
              direction:
                Number(periodData.net_profit.variance) > 0
                  ? intl.formatMessage({ id: 'predict.above', defaultMessage: 'above' })
                  : intl.formatMessage({ id: 'predict.under', defaultMessage: 'under' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.net_profit.variance) > 0,
                    [styles.negativeChange]: Number(periodData.net_profit.variance) < 0
                  })}
                >
                  {`${Number(periodData.net_profit.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.net_profit.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Cash */}
      <li>
        <FormattedMessage
          id="onboarding.target_cash_end_value"
          defaultMessage="Target cash at the end of the period is {currValue}"
          values={{
            currValue: (
              <span className={styles.boldText}>
                {formatCurrency(+periodData.cash.comparison_value, intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />
      </li>
    </ul>
  );
};
